// client_id and scopes are set in _async_init

import $ from 'jquery';

///<reference path="../../../../node_modules/@types/google.accounts/index.d.ts" />

import * as arbitraryEvents from 'js/core_webpack/arbitrary_events';

/*
Google (and facebook.ts) is used in several undeclared dependencies to manage the
Google oAuth process. It relies on _async_init.html.erb to set up a script tag that imports
the google.accounts JS with a callback set to create a singleton of this class as the global Google object.

See these files for usage (may be incomplete):
app/assets/javascripts/auth/oauth_adapter.coffee
app/assets/javascripts/auth/oauth_buttons.coffee
app/assets/javascripts/reg_path/views/name_only_coauthors/name_only_coauthors_header.coffee
app/assets/javascripts/facebook_contacts.coffee
app/views/users/_connected_apps.html.slim
*/
export class GoogleSingleton {
  // The same scopes should also be used in any mobile code that uses Google OAuth.
  static scopes = [
    'openid',
    'profile',
    'email',
    'https://www.googleapis.com/auth/userinfo.profile',
  ].join(' ');

  DISCONNECT_API_URL = 'https://accounts.google.com/o/oauth2/revoke?token=';
  DISCONNECTED_URL = 'auth/disconnect';
  CONNECTED_URL = 'auth/connect';

  client?: google.accounts.oauth2.TokenClient;
  error?: google.accounts.oauth2.ClientConfigError;
  authResponse?: google.accounts.oauth2.TokenResponse;
  // Used to send the token response/error back to the OauthAdapter
  // No field initializer here — authorize() always sets this before use, and $.Deferred
  // is not available in the SSR (Node.js) environment.
  deferred!: JQueryDeferred<google.accounts.oauth2.TokenResponse>;

  init = (client_id: string) => {
    $(document).ready(this.initTokenClient.bind(this, client_id));
  };

  initTokenClient(clientId: string): void {
    const tclient = google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: this.scope(),
      callback: this.handleCredentialResponse.bind(this),
      // "The user will be prompted only the first time your app requests access."
      prompt: '',
      error_callback: this.handleError.bind(this),
    });

    this.setTokenClient(tclient);
  }

  authorize(): JQueryDeferred<google.accounts.oauth2.TokenResponse> {
    // Need to create a new deferred object every time authorize is called, otherwise
    // the original values in deferred.reject & deferred.resolve persist, causing issues.
    this.deferred = $.Deferred<google.accounts.oauth2.TokenResponse, Error>();
    if (!this.client) {
      arbitraryEvents.write('GoogleSigninError', {
        error_type: 'initialization_error',
      });
      this.deferred.reject({
        error: 'Error setting up Google Authentication',
        details: 'Please refresh the page and try again',
      });
    } else if (this.authResponse) {
      this.deferred.resolve(this.authResponse);
    } else {
      this.client.requestAccessToken();
    }
    return this.deferred;
  }

  scope() {
    return GoogleSingleton.scopes;
  }

  disconnectAndReload(): JQueryDeferred<google.accounts.oauth2.TokenResponse> {
    return this.authorize().done((authResponse) => {
      google.accounts.oauth2.revoke(authResponse.access_token, () => {});
      return $.post(
        this.DISCONNECTED_URL,
        {
          provider: 'google',
          'oauth[token]': authResponse.access_token,
          'oauth[expires]': authResponse.expires_in,
        },
        () => window.location.reload(),
      );
    });
  }

  private setTokenClient = (client: google.accounts.oauth2.TokenClient) => {
    this.client = client;
  };

  private handleError = (error: google.accounts.oauth2.ClientConfigError) => {
    arbitraryEvents.write('GoogleSigninError', {
      error_type: error.type,
      error_details: error.message,
    });
    this.deferred.reject({
      error: error.name,
      details: error.message || 'Unknown error, please try again.',
    });
  };

  private handleCredentialResponse(
    response: google.accounts.oauth2.TokenResponse,
  ) {
    if (response.error) {
      this.deferred.reject({
        error: response.error,
        details:
          response.error_description || 'Unknown error, please try again.',
      });
    }
    this.deferred.resolve(response);
  }
}

interface Error {
  error: string;
  details: string;
}

const Google = new GoogleSingleton();

if (typeof window !== 'undefined') {
  // TODO(jpettit): Remove when we get rid of older (mostly coffeescript) dependencies that don't
  // properly import their requirements.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).Google = Google;

  // GoogleClientID is set in google/_async_init.html.erb, if that hasn't run yet, this will return
  // and _async_init will set it up when it runs
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window as any).GoogleClientID) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Google.init((window as any).GoogleClientID);
  } else {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).InitGoogle = Google.init;
  }
}

export default Google;
